import type { ReactNode } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "@/components/ui/dialog";
import { cn } from "@hipotecar/ui/lib/utils";

interface BaseModalProps {
	open: boolean;
	onClose: () => void;
	title: string;
	/** Visually hidden description for screen readers. Defaults to title. */
	description?: string;
	children: ReactNode;
	className?: string;
	onOpenAutoFocus?: (e: Event) => void;
	showCloseButton?: boolean;
	hideTitle?: boolean;
	hideHeader?: boolean;
}

/**
 * Shared modal shell used by all feature-level dialogs.
 * Handles open/close wiring, DialogHeader boilerplate, and accessibility.
 *
 * Usage:
 * ```tsx
 * <BaseModal open={isOpen} onClose={close} title="Add Income">
 *   <MyForm />
 * </BaseModal>
 * ```
 */
export function BaseModal({
	open,
	onClose,
	title,
	description,
	children,
	className,
	onOpenAutoFocus,
	showCloseButton = true,
	hideTitle = false,
	hideHeader = false,
}: BaseModalProps) {
	return (
		<Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
			<DialogContent 
				className={cn("sm:max-w-lg", className)} 
				onOpenAutoFocus={onOpenAutoFocus}
				showCloseButton={showCloseButton}
			>
				<DialogHeader className={hideHeader ? "sr-only" : undefined}>
					<DialogTitle className={hideTitle ? "sr-only" : undefined}>
						{title}
					</DialogTitle>
					<DialogDescription className="sr-only">
						{description ?? title}
					</DialogDescription>
				</DialogHeader>
				{children}
			</DialogContent>
		</Dialog>
	);
}
